"use client";

import { FC, memo, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { IEventFeedResponse, ISportFeedResponse } from "@finbackoffice/websocket-client";
import { sortByScheduledTime } from "@finbackoffice/fe-core";
import { ActiveSportGameContext } from "contexts";
import { Carousel, CarouselSlide } from "components/base/carousel/Carousel";
import SportsLayout from "screens/layouts/sport/common/sports/SportsLayout";
import { FeedScopeEnum } from "utils/constants";
import HomeSportGameTop from "../game/top/HomeSportGameTop";
import styles from "../sport-games.module.sass";

const CAROUSEL_SETTINGS = { loop: true };

type IProps = {
    events: IEventFeedResponse[];
    sports: ISportFeedResponse[];
    activeSportId: number | null;
    onSportClickHandler: (sportId: number) => void;
    autoplayDelay: number;
    feed: FeedScopeEnum;
};

const SportGamesContent: FC<IProps> = ({
    events,
    activeSportId,
    onSportClickHandler,
    autoplayDelay,
    sports,
    feed,
}) => {
    const { setActiveGameState, activeGameState } = useContext(ActiveSportGameContext);

    const activeSportEvents = useMemo(
        () =>
            events
                .filter((event) => event.sport_id === activeSportId)
                .sort((a, b) => sortByScheduledTime(a, b)),
        [activeSportId, events],
    );

    const getActiveTournament = useCallback(
        (gameId: number) => {
            const selectedEvent = activeSportEvents.find((game) => gameId && game.id === gameId);
            const tournamentId = selectedEvent?.tournament_id ?? 0;
            const gamesSnapshot = events.filter((game) => game.tournament_id === tournamentId);

            return { id: tournamentId, gamesSnapshot };
        },
        [activeSportEvents, events],
    );

    const getActiveTournamentRef = useRef(getActiveTournament);
    getActiveTournamentRef.current = getActiveTournament;

    useEffect(() => {
        if (activeGameState.gameId && activeGameState.tournament === null) {
            const gameId = parseInt(activeGameState.gameId);

            setActiveGameState((state) => ({
                ...state,
                tournament: getActiveTournamentRef.current(gameId),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onGameClickHandler = useCallback(
        (gameId: number) => {
            if (gameId) {
                setActiveGameState({
                    gameId: gameId.toString(),
                    tournament: getActiveTournament(gameId),
                });
            }
        },
        [getActiveTournament, setActiveGameState],
    );

    const carouselAutoplaySettings = useMemo(() => ({ delay: autoplayDelay }), [autoplayDelay]);

    return (
        <SportsLayout
            showNoGamesView
            feed={feed}
            variant="widget"
            sports={sports}
            activeSportId={activeSportId ?? undefined}
            onSportClick={onSportClickHandler}>
            {activeSportId !== null && (
                <Carousel
                    key={activeSportId}
                    options={CAROUSEL_SETTINGS}
                    autoplay={carouselAutoplaySettings}
                    wrapperClassName={styles.sportGamesCarousel}>
                    {activeSportEvents.map((game, index) => (
                        <CarouselSlide key={game.id}>
                            <HomeSportGameTop
                                game={game}
                                index={index}
                                onGameClickHandler={onGameClickHandler}
                            />
                        </CarouselSlide>
                    ))}
                </Carousel>
            )}
        </SportsLayout>
    );
};

export default memo(SportGamesContent);
